"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useRef, useState } from "react";
import { cx } from "../internal/cx.js";
/*
 * Components of «Пазл» (edison, «Тренажёр ценностного предложения»; decision 28.10): the big number of a result, a thin
 * progress with its step, a status dot, the «what to fix» rows, an advice plate, the Segmented capsule and a Disclosure.
 * Behaviour — facts model edison_components (П360, П361).
 */
/** «1 из 14»: the number that leads a result. */
export function BigNumber({ value, of, className }) {
    return _jsxs("span", { className: cx("jui-bignum", className), children: [_jsx("b", { children: value }), of !== undefined && _jsxs("span", { children: ["\u0438\u0437 ", of] })] });
}
/** A thin progress line; `step` — «7 из 7» in mono beside it. */
export function Progress({ value, max = 1, label, step, className }) {
    const share = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
    const bar = _jsx("div", { className: cx("jui-progress", !step && className), role: "progressbar", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": max, "aria-valuenow": value, children: _jsx("span", { style: { width: `${share * 100}%` } }) });
    return step === undefined ? bar : _jsxs("div", { className: cx("jui-hstack", className), "data-gap": "3", style: { alignItems: "center" }, children: [_jsx("div", { style: { flex: 1 }, children: bar }), _jsx("span", { className: "jui-step", children: step })] });
}
/** A status as a dot of four tones; the word goes next to it, never the colour alone. */
export function Dot({ tone = "idle", className }) {
    return _jsx("span", { className: cx("jui-dot", tone !== "idle" && `jui-dot--${tone}`, className), "aria-hidden": "true" });
}
/** «Что исправить первым»: a dot, the title with its detail, one action on the right. */
export function IssueList({ items, label, className }) {
    return _jsx("ul", { className: cx("jui-issues", className), "aria-label": label, children: items.map(item => _jsxs("li", { className: "jui-issue", children: [_jsx(Dot, { tone: item.tone ?? "bad" }), _jsxs("span", { children: [_jsx("span", { className: "jui-issue-title", children: item.title }), item.detail && _jsx("div", { className: "jui-issue-detail", children: item.detail })] }), item.action] }, item.key)) });
}
/** A soft accent plate with advice («Как сказать лучше»). */
export function Advice({ label, icon, children, className }) {
    return _jsxs("div", { className: cx("jui-advice", className), children: [label && _jsxs("span", { className: "jui-advice-label", children: [icon, label] }), children] });
}
/**
 * A capsule of options: exactly one is chosen (П360); ←/→ move the choice round, past the disabled ones (П361).
 * A radiogroup: Tab enters on the chosen option.
 */
export function Segmented({ label, options, value, onChange, className }) {
    const box = useRef(null);
    const live = options.filter(option => !option.disabled);
    const move = (dir) => {
        if (live.length === 0)
            return;
        const at = live.findIndex(option => option.value === value);
        const next = live[(at + dir + live.length) % live.length];
        onChange(next.value);
        box.current?.querySelector(`[data-value="${CSS.escape(next.value)}"]`)?.focus();
    };
    const keyDown = (event) => {
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            move(1);
        }
        else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            move(-1);
        }
    };
    return _jsx("div", { ref: box, className: cx("jui-seg", className), role: "radiogroup", "aria-label": label, onKeyDown: keyDown, children: options.map(option => _jsx("button", { type: "button", role: "radio", className: "jui-seg-option", "data-value": option.value, "aria-checked": option.value === value, tabIndex: option.value === value ? 0 : -1, disabled: option.disabled, onClick: () => { if (option.value !== value)
                onChange(option.value); }, children: option.label }, option.value)) });
}
/** «＋ Все проверки»: a heading that opens its body; aria-expanded follows it. */
export function Disclosure({ summary, children, defaultOpen = false, className }) {
    const [open, setOpen] = useState(defaultOpen);
    const id = useId();
    return _jsxs("div", { className: cx("jui-disclosure", className), children: [_jsx("button", { type: "button", className: "jui-disclosure-head", "aria-expanded": open, "aria-controls": id, onClick: () => setOpen(!open), children: summary }), open && _jsx("div", { id: id, children: children })] });
}
/** The list inside «Все проверки»: a dot (or ✓ for passed) and the title, its detail under it. */
export function CheckList({ items, className }) {
    return _jsx("ul", { className: cx("jui-checks", className), children: items.map(item => _jsxs("li", { className: "jui-check", children: [_jsx(Dot, { tone: item.tone ?? "idle" }), _jsx("span", { children: item.title }), item.detail && _jsx("span", { className: "jui-check-detail", children: item.detail })] }, item.key)) });
}
